import {FC, RefObject, useMemo, useState} from 'react';
import {api, IReminder, EReminderStatus, useT, getDateTimeString} from '@kcast-fe/core';
import {Box, Flex} from '@chakra-ui/react';
import {
  Svg,
  Badge,
  QuickMenu,
  TBadgeVariant,
  IQuickMenuItem,
  ConfirmDialog,
  useScreenSize
} from 'ui-kit';

interface IProps {
  reminder: IReminder;
  containerRef: RefObject<HTMLDivElement | null>;
}

const STATUS_VARIANT: Record<EReminderStatus, TBadgeVariant> = {
  [EReminderStatus.Active]: 'success',
  [EReminderStatus.Paused]: 'info',
  [EReminderStatus.Cancelled]: 'error',
  [EReminderStatus.Completed]: 'neutral'
};

const ReminderItemFC: FC<IProps> = ({reminder, containerRef}) => {
  const [confirmCancel, setConfirmCancel] = useState(false);

  const pauseMutation = api.memoryReminders.usePauseReminderMutation();
  const resumeMutation = api.memoryReminders.useResumeReminderMutation();
  const cancelMutation = api.memoryReminders.useCancelReminderMutation();

  const {isLessThan768} = useScreenSize();
  const {t} = useT();

  const isActive = reminder.status === EReminderStatus.Active;
  const isPaused = reminder.status === EReminderStatus.Paused;
  const isDone =
    reminder.status === EReminderStatus.Cancelled || reminder.status === EReminderStatus.Completed;

  const handleCancel = () => {
    cancelMutation.mutate(reminder.id, {onSuccess: () => setConfirmCancel(false)});
  };

  const menuItems = useMemo<IQuickMenuItem<string>[]>(
    () => [
      ...(isActive
        ? [
            {
              id: 'pause',
              name: t('reminders.actions.pause'),
              onClick: () => pauseMutation.mutate(reminder.id)
            }
          ]
        : []),
      ...(isPaused
        ? [
            {
              id: 'resume',
              name: t('reminders.actions.resume'),
              onClick: () => resumeMutation.mutate(reminder.id)
            }
          ]
        : []),
      ...(!isDone
        ? [
            {
              id: 'cancel',
              name: t('reminders.actions.cancel'),
              isDanger: true,
              onClick: () => setConfirmCancel(true)
            }
          ]
        : [])
    ],
    [t, reminder, isActive, isPaused, isDone, pauseMutation, resumeMutation]
  );

  return (
    <>
      <Flex
        align={'flex-start'}
        gap={'8px'}
        p={'16px 0 12px 12px'}
        borderBottom={'thin solid'}
        borderColor={'{colors.borderDefault}'}
        className={'group'}
        _hover={{bg: '{colors.bgSurface}'}}
        transition={'background var(--tr-100-ei)'}
      >
        <Flex direction={'column'} gap={isLessThan768 ? '4px' : '6px'} flex={'1 1 auto'} minW={0}>
          <Flex alignItems={'center'} gap={'8px'}>
            <Badge variant={STATUS_VARIANT[reminder.status]}>
              {t(`reminders.status.${reminder.status}`)}
            </Badge>
            <Box fontWeight={600} color={'{colors.textPrimary}'} className={'cut-text-one'}>
              {reminder.prompt}
            </Box>
          </Flex>

          <Flex flexDirection={'column'} gap={'2px'}>
            <Box pl={'2px'} className={'cut-text-one'}>
              {reminder.recurrence_description}
            </Box>

            <Flex
              pl={'2px'}
              gap={'4px'}
              fontSize={'14px'}
              color={'{colors.textTertiary}'}
              flexWrap={'wrap'}
            >
              {reminder.next_run_at && (
                <>
                  <Box>{t('reminders.fields.nextRun')}:</Box>
                  <Box>{getDateTimeString(reminder.next_run_at, true)}</Box>
                  <Box px={'2px'}>•</Box>
                </>
              )}
              <Box>{t('reminders.fields.runCount')}:</Box>
              <Box>
                {reminder.repeat_count
                  ? `${reminder.run_count}/${reminder.repeat_count}`
                  : reminder.run_count}
              </Box>
            </Flex>
          </Flex>
        </Flex>

        <Flex
          pr={'16px'}
          opacity={isLessThan768 ? 1 : 0}
          _groupHover={{opacity: 1}}
          transition={'opacity var(--tr-150-ei)'}
        >
          <QuickMenu
            hideActive
            container={containerRef}
            trigger={<Svg name={'Dots'} size={'s24'} />}
            items={menuItems}
          />
        </Flex>
      </Flex>

      <ConfirmDialog
        isOpen={confirmCancel}
        title={t('reminders.confirm.cancelTitle')}
        body={t('reminders.confirm.cancelBody')}
        confirmLabel={t('reminders.actions.cancel')}
        variant={'danger'}
        isLoading={cancelMutation.isPending}
        onConfirm={handleCancel}
        onCancel={() => setConfirmCancel(false)}
      />
    </>
  );
};

export const ReminderItem = ReminderItemFC;
